import { createContext, Dispatch, useContext, useEffect, useRef } from 'react';
import { router, usePage } from '@inertiajs/react';
import useLocalStorage from '../hooks/use-local-storage';
import { SonnerHost, showToast } from '@/components/ui/sonner';

export enum ThemeOptions {
    'light',
    'dark',
    'system',
}

export type UiVariant = 'flat' | 'frosted';
export type ViewAsPreferred = 'admin' | 'member' | 'guest' | null;

type UserSettingsContextType = {
    theme: ThemeOptions;
    setTheme: Dispatch<React.SetStateAction<ThemeOptions>>;
    uiVariant: UiVariant;
    setUiVariant: Dispatch<React.SetStateAction<UiVariant>>;
    viewAsPreferred: ViewAsPreferred;
    focusMode: boolean,
    setFocusMode: Dispatch<React.SetStateAction<boolean>>
    clearFocusMode: () => void;
    setViewAsPreferred: Dispatch<React.SetStateAction<ViewAsPreferred>>;
    clearViewAsPreferred: () => void;
};

const UserSettingsContext = createContext<UserSettingsContextType | null>(null);

interface UserSettingsProviderProps {
    children: React.ReactNode;
};
export default function UserSettingsProvider({children}: UserSettingsProviderProps) {
    const pageAny = usePage() as any;
    const { version } = pageAny;
    const [ theme, setTheme, unsetTheme ] = useLocalStorage<ThemeOptions>('theme', ThemeOptions.system)
    const [ uiVariant, setUiVariant ] = useLocalStorage<UiVariant>('ui-variant', 'flat')
    const [ focusMode, setFocusMode, clearFocusMode ] = useLocalStorage<boolean>('focusMode', false)
    const [ viewAsPreferred, setViewAsPreferred, clearViewAsPreferred ] = useLocalStorage<ViewAsPreferred>('view-as.role', null)

    const currentVersion = typeof version === 'string' ? version : null
    const versionRef = useRef<string | null>(currentVersion)
    const hasNotifiedRef = useRef(false)

    useEffect(() => {
        versionRef.current = currentVersion
        hasNotifiedRef.current = false
    }, [currentVersion])

    // Set theme color mode
    useEffect(() => {
        const root = document.documentElement;
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)');

        if (theme === ThemeOptions.light) {
            root.classList.remove('dark');
        }

        if (theme === ThemeOptions.dark) {
            root.classList.add('dark');
        }

        if (theme === ThemeOptions.system) {
            unsetTheme()
            root.classList.toggle('dark', systemDark.matches);
        }

        const handleSystemChange = (event: MediaQueryListEvent) => {
            if (theme === ThemeOptions.system) {
                root.classList.toggle('dark', event.matches);
            }
        };
        systemDark.addEventListener('change', handleSystemChange);

        return () => {
            systemDark.removeEventListener('change', handleSystemChange);
        };
    }, [theme]);

    // Auto-apply preferred ViewAs for real admins (persisted in localStorage)
    useEffect(() => {
        const auth = pageAny?.props?.auth;
        const va = pageAny?.props?.viewAs;
        const isRealAdmin = va?.real === 'admin';

        if (!auth?.loggedIn || !isRealAdmin) return;
        if (!viewAsPreferred) return;

        // If effective differs from preferred, sync it server-side
        const effective = va?.effective ?? va?.real;
        if (effective !== viewAsPreferred) {
            router.post(route('v2.view_as.set'), { role: viewAsPreferred }, { preserveScroll: true });
        }
    }, [viewAsPreferred, pageAny]);

    useEffect(() => {
        if (!currentVersion) {
            return
        }

        let isMounted = true
        let isPolling = false
        const statusUrl = route('v2.status.version')

        const poll = async () => {
            if (!isMounted || isPolling || hasNotifiedRef.current || !versionRef.current) {
                return
            }

            isPolling = true

            try {
                const response = await fetch(statusUrl, {
                    headers: {
                        Accept: 'application/json',
                    },
                    cache: 'no-store',
                })

                if (!response.ok) {
                    return
                }

                const data = (await response.json()) as { version?: string }

                if (!isMounted || hasNotifiedRef.current || !versionRef.current) {
                    return
                }

                if (typeof data.version === 'string' && data.version !== versionRef.current) {
                    hasNotifiedRef.current = true

                    showToast({
                        title: 'Ny version tillgänglig',
                        description: 'Ladda om för att uppdatera till senaste.',
                        action: {
                            label: 'Ladda om',
                            onClick: () => window.location.reload(),
                        },
                        duration: Infinity,
                    })
                }
            } catch (error) {
                // ignore network issues
            } finally {
                isPolling = false
            }
        }

        const intervalId = window.setInterval(poll, 60_000)
        poll()

        return () => {
            isMounted = false
            window.clearInterval(intervalId)
        }
    }, [currentVersion])

    return (
        <UserSettingsContext.Provider value={{
            theme, setTheme,
            uiVariant, setUiVariant,
            focusMode, setFocusMode, clearFocusMode,
            viewAsPreferred, setViewAsPreferred, clearViewAsPreferred
        }}>
            <SonnerHost />
            {children}
        </UserSettingsContext.Provider>
    )
}

export function useUserSettingsContext() {
    const context = useContext(UserSettingsContext);
    if (!context) {
        throw new Error('useUserSettingsContext must be used within a UserSettingsProvider');
    }
    return context;
}
